import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ADMIN_EMAILS } from '../../firebase/config';
import { FaEye, FaEyeSlash, FaGoogle } from 'react-icons/fa';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase/config';
import './Auth.css';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);

      const userCredential = await login(email, password);
      
      if (ADMIN_EMAILS.includes(userCredential.user.email)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
      
    } catch (error) {
      console.error('Erreur de connexion:', error);
      setError('Email ou mot de passe incorrect');
    }
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    try {
      setGoogleLoading(true);
      setError('');
      const result = await signInWithGoogle();
      
      if (ADMIN_EMAILS.includes(result.user.email)) {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Erreur Google:', error);
      setError('Erreur lors de la connexion avec Google');
    }
    setGoogleLoading(false);
  }

  // Fonction pour réinitialiser le mot de passe - Version corrigée
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!resetEmail) {
      setResetError('Veuillez entrer votre email');
      return;
    }

    setResetLoading(true);
    setResetError('');
    setResetMessage('');

    try {
      // Configuration de l'action code settings
      const actionCodeSettings = {
        url: `${window.location.origin}/login`,
        handleCodeInApp: false
      };

      await sendPasswordResetEmail(auth, resetEmail, actionCodeSettings);
      
      setResetMessage(`✅ Un email de réinitialisation a été envoyé à ${resetEmail}`);
      
      setTimeout(() => {
        setResetModalOpen(false);
        setResetEmail('');
        setResetMessage('');
      }, 4000);
      
    } catch (error) {
      console.error('Code erreur:', error.code);
      console.error('Message erreur:', error.message);
      
      // Afficher l'erreur détaillée
      let errorMessage = '';
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = '❌ Aucun compte trouvé avec cet email';
          break;
        case 'auth/invalid-email':
          errorMessage = '❌ Adresse email invalide';
          break;
        case 'auth/too-many-requests':
          errorMessage = '❌ Trop de tentatives. Veuillez réessayer plus tard.';
          break;
        default:
          errorMessage = `❌ Erreur: ${error.message}`;
      }
      setResetError(errorMessage);
    }
    setResetLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>Connexion</h2>
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="votre@email.com"
            />
          </div>
          
          <div className="form-group password-group">
            <label>Mot de passe</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </button>
            </div>
          </div>

          <div className="forgot-password">
            <button 
              type="button"
              className="forgot-password-btn"
              onClick={() => setResetModalOpen(true)}
            >
              Mot de passe oublié ?
            </button>
          </div>

          <button disabled={loading} className="btn-primary" type="submit">
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>

        {/* Séparateur */}
        <div className="separator">
          <span>ou</span>
        </div>

        {/* Bouton Google */}
        <button 
          className="btn-google"
          onClick={handleGoogleSignIn}
          disabled={googleLoading}
        >
          <FaGoogle className="google-icon" />
          {googleLoading ? 'Connexion...' : 'Continuer avec Google'}
        </button>

        <p className="auth-link">
          Pas encore de compte ? <Link to="/register">Inscrivez-vous</Link>
        </p>
      </div>

      {/* Modal Réinitialisation du mot de passe */}
      {resetModalOpen && (
        <div className="modal-overlay" onClick={() => setResetModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Réinitialiser le mot de passe</h3>
              <button className="modal-close" onClick={() => setResetModalOpen(false)}>
                ✕
              </button>
            </div>
            <form onSubmit={handlePasswordReset} className="modal-form">
              <p className="modal-description">
                Entrez votre adresse email et nous vous enverrons un lien pour réinitialiser votre mot de passe.
              </p>
              
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  placeholder="votre@email.com"
                />
              </div>

              {resetError && <div className="error-message">{resetError}</div>}
              {resetMessage && <div className="success-message">{resetMessage}</div>}

              <button 
                type="submit" 
                className="btn-reset"
                disabled={resetLoading}
              >
                {resetLoading ? 'Envoi en cours...' : 'Envoyer le lien de réinitialisation'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;